


















































import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api';
import { DetailsOutline16, RightArrowOutline12 } from '@hb/coffee-icons';
import { Dialog, DialogView } from '@hb/coffee';
import NativeApi from '@/api/native';
import { allSettledPolyfill } from '@/shared/tools/ocr/utils/polyfill';
import {
  queryAlipaySignState,
  applyAlipaySign,
  queryAlipaySignCapitalList,
} from '@/api/repayment-alipay';
import { AlipaySignStateEnum, AlipaySignSceneEnum } from '@/api/repayment-alipay/types';
import platform from '@/shared/utils/platform';
import { getDuplicatePrevent } from '@/hooks/useDuplicatePrevent';
import { ConflictPolicyEnum } from '@hb/duplicate-prevent';
import { ubtSetupModuleExpose, ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';

export default defineComponent({
  name: 'AlipayDeductionEntry',
  components: {
    DetailsOutline16,
    RightArrowOutline12,
    DialogView,
  },
  setup() {
    /** 是否展示组件 */
    const show = ref<boolean>(false);
    /** 签约状态 */
    const signState = ref<AlipaySignStateEnum | null>(null);
    /** 支持的资方列表 */
    const capitalIds = ref<string[]>([]);
    /** 是否显示渠道说明弹窗 */
    const showChannelDialog = ref<boolean>(false);
    /** 轮询定时器ID */
    let pollingTimer: number | null = null;
    /** 防重复执行实例 */
    const duplicatePrevent = getDuplicatePrevent();

    allSettledPolyfill();

    /** 获取埋点公共参数 */
    const getUbtParams = () => ({
      deductionStatus: signState.value,
    });

    /**
     * 签约状态配置
     */
    const statusConfig = computed(() => {
      switch (signState.value) {
        case AlipaySignStateEnum.SIGNED:
          return { text: '已开通' };
        case AlipaySignStateEnum.SIGNING:
          return { text: '开通中' };
        case AlipaySignStateEnum.NOT_SIGNED:
        default:
          return { text: '去开通' };
      }
    });

    /**
     * 清除轮询定时器
     */
    const clearPollingTimer = () => {
      if (pollingTimer !== null) {
        clearTimeout(pollingTimer);
        pollingTimer = null;
      }
    };

    /**
     * 开始轮询签约状态
     */
    const startSignStatusPolling = () => {
      // 先清除之前的定时器
      clearPollingTimer();

      const interval = 2000; // 轮询间隔2秒

      const poll = async () => {
        try {
          const signInfo = await queryAlipaySignState({
            scene: AlipaySignSceneEnum.BANK_CARD,
          });

          signState.value = signInfo.signState;

          // 终止条件：不是签约中状态就停止轮询
          if (signInfo.signState !== AlipaySignStateEnum.SIGNING) {
            clearPollingTimer();
            return;
          }

          // 继续轮询
          pollingTimer = window.setTimeout(poll, interval);
        } catch {
          // 接口失败也继续轮询
          pollingTimer = window.setTimeout(poll, interval);
        }
      };

      // 延迟2秒后开始轮询
      pollingTimer = window.setTimeout(poll, 2000);
    };

    /** 初始化 */
    const init = async () => {
      // 不是哈啰出行 app 或是鸿蒙系统，则不展示组件
      if (!platform.isEasyBike || platform.isHarmony) {
        return;
      }

      // 同时查询签约状态和资方列表
      const [signInfoResult, capitalListResult] = await Promise.allSettled([
        queryAlipaySignState({
          scene: AlipaySignSceneEnum.BANK_CARD,
        }),
        queryAlipaySignCapitalList(),
      ]);

      // 处理签约状态查询结果
      if (signInfoResult.status === 'fulfilled') {
        show.value = signInfoResult.value.show;
        signState.value = signInfoResult.value.signState;

        // 如果展示组件，上报曝光埋点
        if (signInfoResult.value.show) {
          ubtSetupModuleExpose('finance_loan_alipayDeduction', getUbtParams());
        }

        // 如果签约状态为签约中，则立即开始轮询
        if (signInfoResult.value.signState === AlipaySignStateEnum.SIGNING) {
          startSignStatusPolling();
        }
      }

      // 处理资方列表查询结果
      if (capitalListResult.status === 'fulfilled') {
        capitalIds.value = capitalListResult.value.capitalList || [];
      }
    };

    /** 处理签约 */
    const handleSign = duplicatePrevent.execute(async (ctx) => {
      ubtSetupPageButton('finance_loan_alipayDeduction_click', getUbtParams());

      // 1. 申请签约
      const { payUrl } = await applyAlipaySign(ctx.signal);

      // 2. 调用SDK（不需要await）
      NativeApi.signWithAliPay({
        preOrderInfo: payUrl,
        businessType: '26',
      });
      signState.value = AlipaySignStateEnum.SIGNING;
      // 3. 立即开始轮询
      startSignStatusPolling();
    }, {
      conflictPolicy: ConflictPolicyEnum.DiscardNew,
    });

    /** 处理点击 */
    const handleAction = () => {
      if (signState.value === AlipaySignStateEnum.NOT_SIGNED) {
        handleSign();
      } else if (signState.value === AlipaySignStateEnum.SIGNED) {
        Dialog.alert({
          title: '温馨提示',
          message: '请前往 支付宝APP-我的-用户保护中心 关闭服务',
        });
      }
    };

    /** 展示渠道说明 */
    const showChannelDescription = () => {
      ubtSetupPageButton('finance_loan_alipayDeduction_channelDescription', getUbtParams());
      showChannelDialog.value = true;
    };

    onMounted(() => {
      init();
    });

    onBeforeUnmount(() => {
      // 清理定时器，防止内存泄漏
      clearPollingTimer();
    });

    return {
      show,
      signState,
      statusConfig,
      capitalIds,
      showChannelDialog,
      AlipaySignStateEnum,
      handleAction,
      showChannelDescription,
    };
  },
});
